import React, {createContext, useState, useContext, useEffect, useRef} from 'react';
import {authAPI, getCsrfTokenFromServer} from '../../../shared/api/api';

const AuthContext = createContext();
export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const hasCheckedAuth = useRef(false);

    // Проверяем аутентификацию при загрузке приложения
    useEffect(() => {
        // Защита от двойного вызова в StrictMode
        if (!hasCheckedAuth.current) {
            hasCheckedAuth.current = true;
            checkAuth();
        }
    }, []);

    // Раз в 30 секунд проверяем сессию; если не авторизован — выходим
    useEffect(() => {
        if (!isAuthenticated) return;
        const intervalId = setInterval(async () => {
            try {
                const result = await authAPI.checkAuth();
                const stillAuth = result.success && result?.data?.authenticated;
                if (!stillAuth) {
                    await logout();
                    if (result?.data?.access_expired) {
                        window.sessionStorage.setItem(
                            'landgraf:access_expired_message',
                            result.data.message || 'Срок использования приложения истёк.'
                        );
                    }
                }
            } catch {
                await logout();
            }
        }, 30 * 1000);
        return () => clearInterval(intervalId);
    }, [isAuthenticated]);

    const login = async (username, password) => {
        try {
            setLoading(true);
            const result = await authAPI.login(username, password);
            if (result.success) {
                // Нормализуем структуру данных: user_id -> id
                const userData = result.data.data || result.data;
                setUser({
                    ...userData,
                    id: userData.user_id || userData.id,
                });
                setIsAuthenticated(true);
                
                // Получаем CSRF токен после успешного логина
                await getCsrfTokenFromServer();
                
                return {success: true};
            } else {
                return {
                    success: false,
                    error: result.error,
                    access_expired: result.access_expired,
                };
            }
        } catch (error) {
            return {success: false, error: 'Ошибка соединения'};
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Ошибка при выходе:', error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const checkAuth = async () => {
        try {
            const result = await authAPI.checkAuth()
            if (result.success && result?.data?.authenticated) {
                // Нормализуем структуру данных: user_id -> id, добавляем first_name, last_name
                const userData = result.data.data || result.data;
                setUser({
                    ...userData,
                    id: userData.user_id || userData.id,
                });
                setIsAuthenticated(true);
                
                // Получаем CSRF токен если пользователь уже авторизован
                await getCsrfTokenFromServer();
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (err) {
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    const updateUser = (userData) => {
        setUser(prevUser => ({...prevUser, ...userData}));
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        checkAuth,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

